import React from 'react';
import {
  Box, TextField, Typography, Alert, Divider, InputAdornment, Chip,
} from '@mui/material';
import {
  FactCheck as ReviewIcon,
  VpnKey as KeyIcon,
  LocalHospital as HospitalIcon,
  AdminPanelSettings as AdminIcon,
  LockOutlined as LockIcon,
  WarningAmber as WarnIcon,
} from '@mui/icons-material';

import { BRAND, brandFieldSx } from '../../../brand';
import StepHeading from '../StepHeading';
import { FormSection } from '../FormLayout';

// ============================================================================
//  Step 4 — Review & Activate
// ============================================================================
//  Last chance to catch a typo before anything is written. The summary shows
//  the two things that are expensive to change later — the facility name and
//  the administrator's username — with the username visibly flagged as
//  permanent rather than buried in fine print.
//
//  The activation key field only appears when the shipped database carries a
//  key (GET /setup/status → activationRequired). Installs without one skip
//  activation entirely instead of showing a field nobody can fill.

function SummaryRow({ label, value, mono = false, badge }) {
  return (
    <Box sx={{ py: 1.15, display: 'flex', alignItems: 'baseline', gap: 2 }}>
      <Typography sx={{ fontSize: 12.5, color: BRAND.inkMuted, minWidth: 150, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 13.5, fontWeight: 600, color: value ? BRAND.ink : BRAND.inkFaint,
            fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit',
            wordBreak: 'break-word',
          }}
        >
          {value || '—'}
        </Typography>
        {badge}
      </Box>
    </Box>
  );
}

function SummaryCard({ icon: Icon, title, children }) {
  return (
    <Box sx={{ borderRadius: '14px', border: `1px solid ${BRAND.line}`, overflow: 'hidden', height: '100%' }}>
      <Box
        sx={{
          px: 2.25, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.25,
          background: BRAND.navySoft, borderBottom: `1px solid ${BRAND.line}`,
        }}
      >
        <Icon sx={{ fontSize: 18, color: BRAND.navy }} />
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: BRAND.navy, letterSpacing: '.3px' }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ px: 2.25, py: 1, background: '#fff' }}>{children}</Box>
    </Box>
  );
}

function ReviewStep({ facility, admin, activationKey, activationRequired, errors, onChange, submitError }) {
  return (
    <Box>
      <StepHeading
        icon={ReviewIcon}
        title="Review & Activate"
        subtitle="Check the details below. Completing setup creates your facility record and the super-administrator account, then takes you to the sign-in screen."
      />

      {submitError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{submitError}</Alert>
      )}

      {/* Two equal columns on wide screens, stacked below md. Plain CSS grid
          rather than <Grid>: MUI 7's Grid ignores `item`/`xs`/`md`. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 2.5,
          alignItems: 'start',
        }}
      >
        <SummaryCard icon={HospitalIcon} title="FACILITY">
            <SummaryRow label="Name"     value={facility.name} />
            <Divider />
            <SummaryRow label="Type"     value={facility.type} />
            <Divider />
            <SummaryRow label="Location" value={[facility.location, facility.district, facility.region].filter(Boolean).join(', ')} />
            <Divider />
            <SummaryRow label="Email"    value={facility.email} />
            <Divider />
            <SummaryRow label="Phone"    value={facility.phone} />
            <Divider />
            <SummaryRow
              label="Logo"
              value={facility.logo ? 'Uploaded' : 'Not provided'}
              badge={facility.logo ? (
                <Box component="img" src={facility.logo} alt=""
                  sx={{ width: 26, height: 26, objectFit: 'contain', borderRadius: '5px', border: `1px solid ${BRAND.line}` }} />
              ) : null}
            />
        </SummaryCard>

        <SummaryCard icon={AdminIcon} title="SUPER ADMINISTRATOR">
            <SummaryRow label="Full name" value={admin.fullName} />
            <Divider />
            <SummaryRow label="Staff ID"  value={admin.staffId} />
            <Divider />
            <SummaryRow label="Email"     value={admin.email} />
            <Divider />
            <SummaryRow
              label="Username" value={admin.username} mono
              badge={
                <Chip
                  size="small" icon={<LockIcon sx={{ fontSize: 13 }} />} label="Permanent"
                  sx={{
                    height: 20, fontSize: 10.5, fontWeight: 700,
                    background: BRAND.greenSoft, color: BRAND.greenDark,
                    '& .MuiChip-icon': { color: BRAND.greenDark, ml: .75 },
                  }}
                />
              }
            />
            <Divider />
            <SummaryRow label="Password" value="•••••••• (must be changed at first sign-in)" />
        </SummaryCard>
      </Box>

      {/* ── Activation key, only where the install requires one ── */}
      {activationRequired && (
        <FormSection
          title="Activation"
          caption="Supplied by GIGATT APEX when you purchased this system."
        >
          <TextField
            fullWidth required
            label="Activation Key"
            placeholder="Enter the key issued with your purchase"
            value={activationKey}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            error={!!errors.activationKey}
            helperText={errors.activationKey || ' '}
            sx={{ ...brandFieldSx, maxWidth: 480 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <KeyIcon sx={{ fontSize: 19, color: BRAND.navy }} />
                </InputAdornment>
              ),
            }}
          />
        </FormSection>
      )}

      <Alert
        severity="warning" icon={<WarnIcon />}
        sx={{
          mt: 3.5, borderRadius: '12px',
          background: '#FFF8EC', border: `1px solid ${BRAND.warning}44`, color: BRAND.ink,
          '& .MuiAlert-icon': { color: BRAND.warning },
        }}
      >
        Setup runs <strong>once</strong>. After you complete it this wizard is no longer
        reachable, and the administrator username <strong>{admin.username || '—'}</strong> is
        fixed for the life of the system. Make sure it is spelled correctly.
      </Alert>
    </Box>
  );
}

export default ReviewStep;
